import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { twMerge } from 'tailwind-merge'

import ErrorBoundary from '@components/ErrorBoundary'
import { Icon } from '@components/Icon'
import MenuItem from '@components/MenuItem'
import { Profile } from '@components/SelectProfile'
import Weather, { type WeatherProps } from '@components/Weather'
import { Language } from '@controllers/parts/Header'
import MultiColumnMenu from '@controllers/parts/MultiColumnMenu'
import SocialMediaBar from '@controllers/parts/SocialMediaBar'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  setTextColorBasedOnBg,
  getTextColorBasedOnBg,
} from '@helpers/colorUtils'
import {
  trans,
  MAIN_MENU,
  SIDEBAR_MENU,
  OPEN_MENU,
  MAIN_NAVIGATION,
} from '@helpers/translator'
import { removeLanguagePrefixFromUrl } from '@helpers/urlUtils'
import { useTailwindBreakpoint } from '@hooks/useTailwindBreakpoint'
import LogoVisitMonaco from '@images/logo_visitmonaco.svg?react'

gsap.registerPlugin(ScrollTrigger)

/* ------------------------------
   Interfaces et Types
------------------------------- */

export interface MenuItemType {
  id?: string
  icon?: string
  iconName?: string
  label: string
  url?: string
  viewAllLabel?: string
  subItems?: MenuItemType[]
  image?: {
    path: string
    focal_point?: {
      x: number
      y: number
    }
    caption?: string
  }
  bgColor?: string
  isActive?: boolean
  showInMenu?: boolean
  isFavorite?: boolean
  isInstantSearch?: boolean
}

export interface SidebarMenuProps {
  menu: {
    initPath?: string
    items: MenuItemType[]
    defaultImage?: string
  }
  socialLinks: Record<string, any>
  weather: WeatherProps
  languages: Language[]
  profiles: Profile[]
  homeUrl: string
}

interface FooterSocialBarProps {
  className?: string
  socialLinks: Record<string, any>
  isMultiColumnMenuVisible?: boolean
}

interface SidebarBlurryBackdropProps {
  className?: string
}

export const FooterSocialBar: React.FC<FooterSocialBarProps> = React.memo(
  ({ className = '', socialLinks, isMultiColumnMenuVisible = false }) => (
    <div
      className={twMerge(
        `h-[68px] md:pt-8 md:pb-10 md:h-[90px] flex items-center justify-center bg-white ${!isMultiColumnMenuVisible ? 'w-sidebar-mobile md:w-sidebar-desktop' : 'w-sidebar-desktop'} ${className}`,
      )}
    >
      <Icon
        name="monaco_flag_mini"
        size="auto"
        className="absolute -top-[10px] ltr:left-[20px] ltr:md:left-[25px] rtl:right-[20px] rtl:md:right-[25px]"
      />
      <SocialMediaBar
        socialMediaLinks={socialLinks}
        className={`w-full py-[22px] ${isMultiColumnMenuVisible ? 'flex' : 'hidden md:flex'}  text-black `}
      />
    </div>
  ),
)

export const SidebarBlurryBackdrop: React.FC<SidebarBlurryBackdropProps> = ({
  className,
}) => {
  return (
    <div
      className={twMerge(
        `absolute inset-0 z-10 w-sidebar-mobile md:w-sidebar-desktop backdrop-blur-[150px] ${className}`,
      )}
    />
  )
}

/* ------------------------------
   Fonctions Helpers
------------------------------- */

// Retourne le chemin de l'item (les segments d'URL) s'il est trouvé dans le menu
function findMenuPathSegments(
  items: MenuItemType[],
  targetItem: MenuItemType,
): string[] | null {
  const path: string[] = []

  const helper = (items: MenuItemType[], acc: string[]): boolean => {
    for (const item of items) {
      // Normaliser l'URL en supprimant les préfixes linguistiques
      const segment = item.url
        ? removeLanguagePrefixFromUrl(item.url).replace(/^\//, '')
        : ''
      const newAcc = [...acc, segment]

      if (item === targetItem) {
        path.push(...newAcc)
        return true
      }
      if (item.subItems && item.subItems.length > 0) {
        if (helper(item.subItems, newAcc)) {
          return true
        }
      }
    }
    return false
  }

  return helper(items, []) ? path : null
}

// Recherche récursive de l'item actif en utilisant la propriété isActive
function findDeepActiveMenuItem(
  items: MenuItemType[],
): MenuItemType | undefined {
  let activeItem: MenuItemType | undefined
  for (const item of items) {
    // Si l'item est actif, on le note
    if (item.isActive) {
      activeItem = item
    }
    // Si l'item possède des sous-items, on cherche récursivement
    if (item.subItems && item.subItems.length > 0) {
      const deepActive = findDeepActiveMenuItem(item.subItems)
      if (deepActive) {
        activeItem = deepActive
      }
    }
  }
  return activeItem
}

/* ------------------------------
   Custom Hooks
------------------------------- */

// Animation d'apparition du menu multi-colonne
function useMultiColumnMenuAnimation(isVisible: boolean): void {
  useEffect(() => {
    if (!isVisible) return
    gsap.fromTo(
      '.popover-panel',
      { opacity: 0 },
      { opacity: 1, duration: 0.4, ease: 'power2.out' },
    )
  }, [isVisible])
}

// Déclenche les changements de couleur en fonction des sections dans le scroll
function useScrollColorTriggers(
  setTextColor: React.Dispatch<React.SetStateAction<string>>,
  hasImageState: boolean,
  isMobile: boolean,
): void {
  useEffect(() => {
    const desktopSections = gsap.utils.toArray<HTMLElement>('.block-section')
    const mobileSections = gsap.utils.toArray<HTMLElement>(
      '.mobile-block-section',
    )

    const navLinks = gsap.utils.toArray<HTMLElement>('.sidebar-elmt')
    const sections = isMobile
      ? [...mobileSections, ...desktopSections]
      : desktopSections

    if (!sections.length || !navLinks.length) return

    const triggers: ScrollTrigger[] = []
    sections.forEach((section) => {
      navLinks.forEach((navLink) => {
        const handleColorChange = () => {
          const bgColor = window.getComputedStyle(section).backgroundColor
          if (!bgColor) return
          setTextColorBasedOnBg(navLink, bgColor, section)
          setTextColor(getTextColorBasedOnBg(bgColor, section))
        }

        const st = ScrollTrigger.create({
          trigger: section,
          start: () =>
            `top ${
              navLink.getBoundingClientRect().y +
              navLink.getBoundingClientRect().height / 2
            }`,
          end: () =>
            `bottom ${
              navLink.getBoundingClientRect().y +
              navLink.getBoundingClientRect().height / 2
            }`,
          scrub: 1,
          onEnter: handleColorChange,
          onEnterBack: handleColorChange,
        })
        triggers.push(st)
      })
    })

    return () => {
      triggers.forEach((trigger) => trigger.kill())
    }
  }, [setTextColor, hasImageState])
}

function useBlockSectionHasImage(): boolean {
  const [hasImage, setHasImage] = useState<boolean>(false)

  useEffect(() => {
    const checkHasImage = () => {
      const blockSection = document.querySelector('.block-section')
      if (blockSection) {
        setHasImage(blockSection.classList.contains('has-image'))
      }
    }

    const observer = new MutationObserver(() => {
      checkHasImage()
    })

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class'],
    })

    // Vérification initiale
    checkHasImage()

    return () => observer.disconnect()
  }, [])

  return hasImage
}

/* ------------------------------
   Composant SidebarMenu
------------------------------- */
const SidebarMenuContent: React.FC<SidebarMenuProps> = ({
  menu = { initPath: '', items: [] },
  socialLinks = {},
  weather = {},
  languages = [],
  profiles = [],
  homeUrl,
}) => {
  // États locaux

  const [activePath, setActivePath] = useState<string>(menu.initPath || '')
  const [isMultiColumnMenuVisible, setIsMultiColumnMenuVisible] =
    useState<boolean>(false)
  const [activateSearch, setActivateSearch] = useState<boolean>(false)
  const [textColor, setTextColor] = useState<string>('text-white')
  const [sidebarBg, setSidebarBg] = useState<string>('')
  const isMobile = useTailwindBreakpoint('md-')

  // Nouvel état pour afficher le menu uniquement après vérification
  const [isMenuReady, setIsMenuReady] = useState<boolean>(false)

  const hasImageState = useBlockSectionHasImage()

  // Références
  const popoverButtonRef = useRef<HTMLButtonElement | null>(null)
  const menuRefs = useRef<
    (HTMLDivElement | HTMLButtonElement | HTMLAnchorElement | null)[]
  >([])
  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const sidebarBgRef = useRef<HTMLDivElement | null>(null)

  // Détermine l'item actif lors du montage ou au changement du menu
  useEffect(() => {
    const activeItem = findDeepActiveMenuItem(menu.items)
    setActivePath(activeItem?.url || menu.initPath || '')
  }, [menu.initPath, menu.items])

  useEffect(() => {
    if (isMenuReady) {
      document.querySelector('.content-body')?.classList.remove('opacity-0')
    }
  }, [isMenuReady])

  useEffect(() => {
    if (hasImageState) {
      setSidebarBg('')
      setTextColor('text-white')
    } else {
      setSidebarBg('lg:bg-gradient-to-b lg:from-brand lg:to-brand-light')
      setTextColor('text-black')
    }
    if (!isMenuReady) {
      setTimeout(() => {
        setIsMenuReady(true)
      }, 300)
    }
  }, [hasImageState])

  useScrollColorTriggers(setTextColor, hasImageState, isMobile)
  useMultiColumnMenuAnimation(isMultiColumnMenuVisible)

  // Gestion du clic sur un item de menu
  const handleMenuItemClick = useCallback(
    (item: MenuItemType): void => {
      if (item.iconName === 'search' || item.isInstantSearch) {
        // Si c'est un élément de recherche, ouvrir le menu et activer la recherche
        if (popoverButtonRef.current) {
          popoverButtonRef.current.click()
          setActivateSearch(true)
        }
        return
      }

      if (
        item.subItems &&
        item.subItems.length > 0 &&
        popoverButtonRef.current
      ) {
        // Ouvre le menu multi-colonne
        popoverButtonRef.current.click()
        const pathSegments = findMenuPathSegments(menu.items, item)
        if (pathSegments) {
          const pathCombined = pathSegments.join('/')
          const initPath = menu.initPath ?? ''
          const newPath = pathCombined.startsWith(initPath)
            ? pathCombined
            : `${initPath.replace(/\/$/, '')}/${pathCombined.replace(/^\//, '')}`
          setActivePath(newPath)
        }
      }
    },
    [menu.items, menu.initPath],
  )

  // Calcul des sections et items affichés dans la sidebar
  const { sidebarItems, menuItems } = useMemo(() => {
    const menuItemsArray: MenuItemType[] = menu.items

    const itemsWithInitPath: MenuItemType[] = []
    menu.items.forEach((item) => {
      if (item.url && item.url.startsWith(menu.initPath ?? '')) {
        item.subItems?.forEach((subItem) => itemsWithInitPath.push(subItem))
      }
    })

    return {
      sidebarItems: itemsWithInitPath,
      menuItems: menuItemsArray,
    }
  }, [menu.items, menu.initPath, activePath])

  // Gestion de la fermeture du menu multi-colonne
  const handleMenuClose = useCallback((): void => {
    // Simule un clic pour fermer le menu multi-colonne
    popoverButtonRef.current?.click()
    // Met à jour l'état pour masquer le menu
    setIsMultiColumnMenuVisible(false)
    // Réinitialise l'état de recherche
    setActivateSearch(false)
  }, [])

  return (
    <>
      <Popover>
        <PopoverPanel
          modal
          className="popover-panel fixed inset-0 opacity-0 w-full flex origin-top flex-col transition duration-200 ease-out data-[closed]:opacity-0 z-[2000] pointer-events-auto"
          aria-labelledby="menu-heading"
          role="dialog"
        >
          <h2 id="menu-heading" className="sr-only">
            {trans(MAIN_MENU)}
          </h2>
          <ErrorBoundary>
            <MultiColumnMenu
              initPath={activePath}
              onClose={handleMenuClose}
              menuItems={menuItems}
              socialLinks={socialLinks}
              activateSearch={activateSearch}
              Footer={() => (
                <FooterSocialBar
                  className="absolute bottom-0"
                  socialLinks={socialLinks}
                  isMultiColumnMenuVisible={isMultiColumnMenuVisible}
                />
              )}
              languages={languages}
              profiles={profiles}
            />
          </ErrorBoundary>
        </PopoverPanel>

        <div
          ref={sidebarRef}
          className={twMerge(
            'relative h-full w-sidebar-mobile md:w-sidebar-desktop top-0 left-0 rtl:left-auto rtl:right-0 text-black flex flex-col justify-between z-[1000] min-h-screen transition-opacity duration-300',
            !isMenuReady ? 'opacity-0' : 'opacity-1',
          )}
          role="navigation"
          aria-label={trans(SIDEBAR_MENU)}
        >
          <div
            ref={sidebarBgRef}
            className={twMerge('absolute inset-0', sidebarBg)}
          />
          <div className="relative flex items-center md:items-start pt-4 md:p-6 md:pb-0 md:w-full flex-grow flex-col justify-between ">
            <div className="pb-4 flex flex-col justify-between flex-grow">
              <div className="flex flex-col space-y-14">
                <div className="flex flex-col space-y-7">
                  <PopoverButton
                    ref={popoverButtonRef}
                    className="sidebar-elmt z-50 transition-colors duration-400"
                    aria-label={trans(OPEN_MENU)}
                    onClick={() => {
                      const activeItem = findDeepActiveMenuItem(menu.items)
                      setActivePath(activeItem?.url || menu.initPath || '')
                      setIsMultiColumnMenuVisible((prev) => !prev)
                    }}
                  >
                    <Icon name="burger" />
                  </PopoverButton>
                  <div className="hidden md:block" aria-hidden="true">
                    <a href={homeUrl}>
                      <LogoVisitMonaco
                        className="w-[96px] text-black sidebar-elmt transition-colors duration-400"
                        aria-label="Visit Monaco"
                      />
                    </a>
                  </div>
                </div>

                <nav
                  className="space-y-5 hidden md:block"
                  aria-label={trans(MAIN_NAVIGATION)}
                >
                  {sidebarItems.length > 0 &&
                    sidebarItems.map((item, index) => (
                      <MenuItem
                        key={`menu-item-${item.id || index}`}
                        item={item}
                        onClick={() => handleMenuItemClick(item)}
                        ref={(el) => (menuRefs.current[index] = el)}
                        isMultiColumn={false}
                        isActive={item.isActive}
                        className="text-sm hover:font-bold transition-all duration-100"
                      />
                    ))}
                </nav>
              </div>

              <div>
                <Weather
                  {...weather}
                  className="hidden md:flex mt-20 sidebar-elmt"
                />
              </div>
            </div>

            <div className="relative z-20 -mx-6">
              <FooterSocialBar
                className="z-[40] -bottom-[0.3px] "
                socialLinks={socialLinks}
              />
            </div>
          </div>
        </div>
      </Popover>
    </>
  )
}

export const SidebarMenu: React.FC<SidebarMenuProps> = ({
  menu = { initPath: '', items: [] },
  socialLinks = {},
  weather = {},
  languages = [],
  profiles = [],
  homeUrl,
}) => {
  return (
    <ErrorBoundary>
      <SidebarMenuContent
        menu={menu}
        socialLinks={socialLinks}
        weather={weather}
        languages={languages}
        profiles={profiles}
        homeUrl={homeUrl}
      />
    </ErrorBoundary>
  )
}
export default SidebarMenu
